/**
 * אימות דו-שלבי — the challenge. `packages/authn/src/totp.ts`, `recovery-codes.ts`.
 *
 * ── THE FOUR ANSWERS, AND WHY THREE OF THEM READ THE SAME ───────────────────────────────────────
 *
 * `verifyTotp` distinguishes `malformed`, `incorrect` and `replayed`, and the third is the
 * interesting one: a TOTP code is valid for its whole window — ninety seconds here — so a code seen
 * over a shoulder, captured from a phishing page or read out of a log is reusable for the rest of
 * that window unless the server refuses to accept the same step twice. `lastAcceptedStep` is a
 * REQUIRED parameter for that reason; there is no overload that omits it.
 *
 * The user-facing text for `incorrect` and `replayed` is deliberately IDENTICAL. Telling somebody
 * "that code was already used" tells an attacker that the code was right, which is the one fact the
 * replay defence exists to withhold. `malformed` is different because it is the user's own typing
 * and saying so helps them; it reveals nothing.
 *
 * ── NOTHING HERE DECIDES ANYTHING ───────────────────────────────────────────────────────────────
 *
 * The code is posted; the server answers. This screen holds no secret, compares nothing, and counts
 * no attempts — rate limiting is the gate's stage, and a counter in a browser is a counter an
 * attacker owns.
 *
 * ── AND A RESET NEVER ISSUES A SESSION ──────────────────────────────────────────────────────────
 *
 * `resetGrantsSession()` returns a constant `false` on the server so that "reset does not bypass
 * MFA" is a named, tested fact rather than an absence of code. Which is why the alternative offered
 * below is a RECOVERY CODE — a second factor — and never "send me an email to get in".
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { StateBlock } from '../components/StateBlock';
import { TOTP_DIGITS } from '../api/contract';
import type { MfaChallengeOutcome } from '../api/contract';
import { callOperation } from '../api/operations';
import { routes } from '../routes';

const TITLE_HE = 'אימות דו-שלבי';

/**
 * `incorrect` and `replayed` are the same sentence on purpose. A different message for a reused code
 * confirms that the code was correct, which is exactly what the replay defence withholds.
 */
export const CHALLENGE_TEXT_HE: Readonly<
  Record<MfaChallengeOutcome, { readonly titleHe: string; readonly bodyHe: string }>
> = {
  accepted: {
    titleHe: 'האימות הושלם',
    bodyHe: 'ההזדהות אושרה.',
  },
  malformed: {
    titleHe: 'הקוד אינו בפורמט הנכון',
    bodyHe: `קוד האימות מורכב מ-${TOTP_DIGITS} ספרות בלבד.`,
  },
  incorrect: {
    titleHe: 'הקוד אינו נכון',
    bodyHe: 'הקוד שהוזן אינו מתאים. אפשר להמתין לקוד הבא ביישומון ולנסות שוב.',
  },
  replayed: {
    titleHe: 'הקוד אינו נכון',
    bodyHe: 'הקוד שהוזן אינו מתאים. אפשר להמתין לקוד הבא ביישומון ולנסות שוב.',
  },
};

/**
 * THE SCREEN NOW POSTS. **Phase 2 — the defect `operations.ts` opens by naming.**
 *
 * *"`MfaChallengeScreen.tsx` exists, and its submit handler sets a local boolean; `auth.mfa.verify`
 * was certified reachable for months."* That is the exact defect the operation registry was built to
 * make impossible to repeat, and it was still true of this screen — the one on the sign-in path, so
 * an office whose policy requires a second factor could not get into the product at all.
 *
 * ── WHAT THE SCREEN IS ALLOWED TO KNOW ─────────────────────────────────────────────────────────
 *
 * Nothing. Every failure — unknown challenge, expired challenge, wrong code, REPLAYED code, spent
 * recovery code — is the same 401, deliberately, so the endpoint cannot be used to learn which was
 * true. So this screen renders ONE refusal for all of them and does not try to be helpful about
 * which; a screen that guessed would be reconstructing the distinction the endpoint removes.
 *
 * The challenge reference comes from the ADDRESS, because sign-in put it there: the principal holds
 * no cookie yet, so there is no session to carry it and nowhere else it could live. A missing one
 * is a person who reached this screen without signing in, and the screen says so rather than posting
 * an empty reference and reading the generic refusal back.
 *
 * It arrives as a PROP, read by `<MfaChallengeRoute>` in `App.tsx` — the same shape as every other
 * parameterised screen here. A screen that called `useSearchParams` itself would need a `<Router>`
 * around it to render at all, which makes the router a dependency of testing the second factor.
 */
export interface MfaChallengeScreenProps {
  /** From `?challenge=` — `null` when somebody reached this address without signing in. */
  readonly challengeRef: string | null;
}

export function MfaChallengeScreen({ challengeRef }: MfaChallengeScreenProps): JSX.Element {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<'verified' | 'refused' | null>(null);

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (challengeRef === null || sending) return;
    setSending(true);
    setOutcome(null);
    void callOperation('auth.mfa.verify', {
      // `factor` states which one is being presented. See the declared body for why it is not
      // inferred from the code.
      body: { challengeRef, code, factor: useRecovery ? 'recovery_code' : 'totp' },
    })
      .then(
        () => {
          setOutcome('verified');
          // The session cookie arrived with the response. A full navigation rather than a client
          // route, because everything downstream re-reads the session from the server and the
          // simplest way to be sure of that is to start the app again.
          window.location.assign(routes.companies);
        },
        () => {
          // One refusal for every reason. See this screen's header.
          setOutcome('refused');
        },
      )
      .finally(() => setSending(false));
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      provenanceHe="בדיקת הקוד נעשית בשרת בלבד. הדפדפן אינו מחזיק את הסוד ואינו סופר ניסיונות."
    >
      <p className="screen-lead">
        {useRecovery
          ? 'הזן אחד מקודי השחזור ששמרת בעת הרישום. כל קוד שחזור תקף לשימוש אחד בלבד.'
          : 'הזן את הקוד המוצג כעת ביישומון האימות שלך.'}
      </p>

      <form className="form" onSubmit={submit}>
        <label className="field">
          <span className="field__label">{useRecovery ? 'קוד שחזור' : 'קוד מהיישומון'}</span>
          <input
            className="field__input"
            dir="ltr"
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            {...(useRecovery ? {} : { maxLength: TOTP_DIGITS })}
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            required
          />
        </label>

        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending || challengeRef === null}>
            {sending ? 'בודק…' : 'אימות והמשך'}
          </button>
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              setUseRecovery(!useRecovery);
              setCode('');
              setOutcome(null);
            }}
          >
            {useRecovery ? 'חזרה לקוד מהיישומון' : 'אין לי גישה ליישומון — שימוש בקוד שחזור'}
          </button>
        </div>
      </form>

      {challengeRef === null ? (
        <StateBlock
          titleHe="אין אתגר פתוח"
          // Not "your code is wrong" — nothing was checked. The person reached this address without
          // a sign-in that issued a challenge, and the only thing to do is start again.
          bodyHe="הגעת למסך הזה בלי אתגר פתוח, ולכן אין מה לבדוק. יש להתחיל את ההתחברות מחדש."
          tone="unavailable"
          nextStep={
            <a className="btn btn--primary" href={routes.signIn}>
              מעבר להתחברות
            </a>
          }
        />
      ) : null}

      {outcome === 'refused' ? (
        <StateBlock
          titleHe={CHALLENGE_TEXT_HE.incorrect.titleHe}
          bodyHe={CHALLENGE_TEXT_HE.incorrect.bodyHe}
          tone="error"
          nextStep={
            <a className="btn" href={routes.mfaEnrolment}>
              חזרה למסך הרישום
            </a>
          }
        />
      ) : null}

      <section className="notice" data-tone="info">
        <h2 className="notice__title">איפוס סיסמה אינו מדלג על השלב הזה</h2>
        <p className="notice__body">
          גם לאחר איפוס סיסמה נדרש הגורם השני. אחרת תיבת הדואר הופכת לדרך עוקפת לכניסה למערכת, ולכן
          אין מסלול שמוותר על האימות.
        </p>
      </section>
    </AppShell>
  );
}
